// Mock data for Sadasiva Velkuri Portfolio

export const portfolioData = {
  personalInfo: {
    name: "Sadasiva Velkuri",
    title: "Data Principal | Data Architect",
    tagline: "Driving operational efficiency through data architecture, governance, and AI/ML innovations.",
    location: "Hyderabad, India 500075",
    yearsExperience: "19+",
    projectsCompleted: "20+",
    availability: "Available for new opportunities",
    email: "sadasiva84@gmail.com",
    phone: "+91-9618276444",
    linkedin: "linkedin.com/in/sadasiva-velkuri",
    resumeUrl: "/api/resume/download"
  },

  about: {
    professionalSummary: "Accomplished Data Principal at Carrier Technologies with 19 years of experience spanning data architecture, master data management, governance, integration, and AI/ML — with a proven track record of turning trusted data into strategic business outcomes. Delivered measurable impact by implementing Medallion Data Architecture and AI-enabled onboarding and customer experience platforms — accelerating supplier and customer onboarding, improving personalization at scale, strengthening compliance, and generating a 35% ROI through cost savings, operational efficiency, and revenue growth. Known for cross-functional partnership, technical mentorship, and building high-performing teams that improve data quality, reliability, and readiness for future challenges.",
    
    coreStrengths: [
      "Data leadership — inspiring and scaling high-performing, cross-functional data teams across engineering, product, and AI/ML disciplines",
      "Data strategy — defining vision, executive alignment, and enterprise-wide data roadmaps that translate business objectives into actionable data initiatives",
      "Data architecture and integration (enterprise and cloud — AWS, GCP: S3, Redshift, BigQuery)",
      "Master Data Management domains: Party (Customer, Vendor, Member), Product, Material, Account, Location",
      "MDM platforms: IBM MDM Advanced & Collaboration Edition, Informatica MDM, SAP MDG",
      "Data governance, quality, profiling, and stewardship (Collibra, Alation, Informatica DG, Atlan)",
      "Enterprise search engines (ElasticSearch, MongoDB)",
      "Dimensional modeling (star and snowflake schemas)",
      "Performance tuning, load testing, and test automation",
      "Strategic roadmaps, standards definition, and reusable global templates",
      "Delivery management, risk escalation, and stakeholder communication"
    ],
    
    leadershipHighlights: [
      "Defined and executed enterprise data strategy at Carrier Technologies, aligning data architecture, governance, and AI/ML initiatives to organizational goals and delivering a 35% ROI.",
      "Led end-to-end delivery across requirements, design, build, testing, implementation, and production support for large-scale MDM and data integration programs.",
      "Architected and governed data ecosystems integrating Medallion Architecture with AI-driven platforms, enabling faster onboarding, stronger compliance, and measurable revenue uplift.",
      "Partnered with enterprise architecture teams to define and enforce standards for MDM and critical data use cases across the organization.",
      "Coached and mentored cross-functional delivery teams — including data engineers, product owners, scrum masters, and AI/ML engineers — on data usage, propagation methods, and governance best practices to drive consistency and adoption.",
      "Delivered reusable, scalable solutions adopted as global templates across multiple teams and geographies."
    ],
    
    beyondWork: "Outside of architecting and building data platforms, I enjoy exploring new coffee shops, reading about system design, and contributing to open-source tools. I believe the strongest products come from teams that value clear communication, empathy, and continuous learning."
  },

  expertise: [
    {
      title: "Data Architecture & Strategy",
      description: "Medallion Architecture, Data Fabric, Data Mesh, Data Lakehouse, Data Lake, Data as a Product, Data Marketplace, Data Democratization & Literacy."
    },
    {
      title: "Mission-Critical Systems",
      description: "High-available, fault-tolerant, real-time distributed pipelines & systems that power core business operations and 24/7 decision making."
    },
    {
      title: "AI/ML Implementation, Ops & Governance",
      description: "AI/ML lifecycle management, Data Governance frameworks (Data Availability, Data Quality) to ensure model accuracy, security and ethical compliance at scale."
    },
    {
      title: "ROI-Driven Digital/Data/ERP Transformation",
      description: "Translating complete data capabilities into measurable business outcomes, focusing on cost optimization and revenue uplift through technical leadership."
    }
  ],

  workExperience: [
    {
      id: 1,
      title: "Data Principal",
      company: "Carrier Technologies",
      period: "07/2024 – Present",
      responsibilities: [
        "Built a unified Medallion Data Architecture (bronze, silver, gold layers) to consolidate signals across customers, suppliers, and operational touchpoints.",
        "Led the design of end-to-end architectures, integrating MDM with customer and supplier onboarding platforms.",
        "Defined workflow, integration, and API-driven patterns that extend enterprise platforms, ensuring compliance with security and regulatory standards.",
        "Partnered with product and engineering leaders to translate business requirements into technical designs, evolving architectural standards and frameworks.",
        "Architected AI/ML capabilities for personalization, semantic search, recommendations, and conversational AI agents.",
        "Established governance frameworks for data quality, privacy, and compliance (GDPR, CCPA), ensuring readiness for AI-driven workflows.",
        "Developed multi-year architectural roadmaps aligned with manufacturing industry priorities.",
        "Provided thought leadership on emerging technologies (LLMs, multi-agent systems, generative AI) and their application in manufacturing CX.",
        "Enabled supplier onboarding automation with AI agents—resulting in 40% cost savings on onboarding time for supplier/customer setup.",
        "Drove data-driven decision-making to strengthen supply chain resilience, achieving 30% improvement in data quality and reduction in cycle time."
      ]
    },
    {
      id: 2,
      title: "Senior Manager – MDM Architect",
      company: "Charles Schwab",
      period: "04/2022 – 06/2024",
      responsibilities: [
        "Led integrations to onboard MDM APIs across multiple enterprise applications, replacing legacy APIs and enhancing scalability and efficiency.",
        "Led large-scale conversion project mastering 15 million customers (Retail & Corporate) with new systems, tools, and processes.",
        "Developed database architectural strategies at modeling, design, and implementation stages to address business requirements.",
        "Created multi-year roadmap for MDM, data quality, and data acquisitions to align with business goals.",
        "Collaborated with Enterprise & system architects, design analysts, and others to understand business and industry requirements.",
        "Implemented operational standards, policies, and procedures to streamline processes and ensure compliance.",
        "Performed performance tuning of real-time and batch loads at different levels—MDM Server, Match and Merge Process, and Database.",
        "Led CI/CD implementation of Database, ETL, and MDM components, reducing costs and code migration time by collaborating with DevOps teams.",
        "Received Top Performer Award 2022 for outstanding contributions."
      ]
    },
    {
      id: 3,
      title: "Data Governance Architect",
      company: "Dell Technologies",
      period: "06/2021 – 03/2022",
      responsibilities: [
        "Created Data Governance Reference Architecture for enterprise-wide adoption.",
        "Identified use cases for Data Governance across data catalog, data lineage, and data privacy.",
        "Identified Data Quality and Data Governance KPIs/Metrics to measure program effectiveness.",
        "Created remediation processes to enhance data quality across the organization.",
        "Delivered Data Dictionary Framework to define composite data dictionary elements and related physical data elements.",
        "Developed RFPs and proof of concepts (POCs) to guide vendor selection and secure executive buy-in."
      ]
    },
    {
      id: 4,
      title: "Data Architect",
      company: "Deloitte",
      period: "10/2019 – 04/2021",
      responsibilities: [
        "Developed database architectural strategies for modeling, design, and implementation to meet business requirements.",
        "Migrated legacy systems to new technologies, enhancing efficiency and reducing costs across operations.",
        "Established Reference Architecture for Multi Domain MDM.",
        "Created multi-site system architecture plans, eliminating redundancy across the organization.",
        "Gathered and refined requirements, leading project design and overseeing implementation.",
        "Facilitated Data Governance and MDM workshops with stakeholders to align on strategies.",
        "Collaborated with architects and analysts to understand business and industry requirements effectively.",
        "Designed process improvements to automate tasks, reducing data delivery times significantly."
      ]
    },
    {
      id: 5,
      title: "MDM Lead Developer",
      company: "AAA Insurance Company",
      period: "08/2016 – 04/2019",
      responsibilities: [
        "Gathered and analyzed requirements to inform design and development of MDM technology solutions.",
        "Designed scalable enterprise search solution using Elasticsearch for insurance applications including Policy Center and Agent Center.",
        "Executed data analysis and conducted root cause investigations for matching, survivorship, and suspect processing use cases.",
        "Optimized performance tuning for real-time and batch loads across multiple MDM server levels.",
        "Containerized MDM WAS servers using OpenShift to achieve 99.8% MDM application availability.",
        "Converted ETL jobs from Informatica to Spring Boot, increasing system efficiency.",
        "Reviewed team-developed code to verify adherence to quality standards in ETL, MDM, and data quality processes."
      ]
    },
    {
      id: 6,
      title: "MDM/ETL Lead",
      company: "Softpath Systems / Cognizant Technology Solutions",
      period: "06/2006 – 07/2016",
      responsibilities: [
        "Designed and developed Customer and Product Information Master for enhanced data management.",
        "Customized IBM's Out-of-the-Box Data Model to align with Canonical Data Architecture.",
        "Converted stored procedures into efficient mappings for improved data processing.",
        "Developed mappings for loading data into slowly changing dimensions and Fact Tables.",
        "Created Claims Physical Data Model transitioning from legacy systems to Star Schema.",
        "Monitored trends in MDM technologies to inform organizational strategy and direction.",
        "Implemented innovative solutions to navigate complex challenges and ensure timely delivery.",
        "Received Technical Guru Award 2011 at Cognizant."
      ]
    }
  ],

  skills: {
    "Data Architecture & MDM": [
      "IBM MDM AE 11x",
      "Informatica MDM 10x",
      "Medallion Architecture",
      "Master Data Management",
      "Multi-Domain MDM",
      "Data Modeling",
      "Data Integration",
      "ETL"
    ],
    "Data Governance & Quality": [
      "Collibra DGC",
      "Data Governance",
      "Data Quality",
      "Informatica IDQ",
      "GDPR / CCPA",
      "Data Lineage",
      "Data Catalog",
      "Data Privacy"
    ],
    "AI / ML & Search": [
      "AI/ML Implementation",
      "Elasticsearch 5.x",
      "LLMs",
      "Conversational AI",
      "Semantic Search",
      "Generative AI",
      "Multi-Agent Systems",
      "Personalization"
    ],
    "Cloud & DevOps": [
      "AWS",
      "OpenShift",
      "Docker",
      "CI/CD",
      "Jenkins",
      "Maven",
      "Hadoop",
      "Cloud Technology"
    ],
    "Languages": [
      "SQL",
      "PL/SQL",
      "Java",
      "Spring Boot",
      "Python",
      "C",
      "C++"
    ],
    "Databases": [
      "Oracle",
      "DB2",
      "SQL Server",
      "PostgreSQL"
    ],
    "Tools & Platforms": [
      "Informatica",
      "Tableau",
      "Cognos",
      "Splunk",
      "Dynatrace",
      "Erwin",
      "Git",
      "SVN"
    ]
  },

  projects: [
    {
      id: 1,
      title: "Medallion Data Architecture – Carrier Technologies",
      role: "Data Principal",
      description: "Designed and implemented a unified Medallion Data Architecture (bronze, silver, gold layers) integrating MDM with customer/supplier onboarding platforms, enabling AI-driven personalization and governance compliant with GDPR/CCPA.",
      responsibilities: [
        "Architected end-to-end data platform across bronze, silver, and gold layers",
        "Integrated MDM with customer and supplier onboarding automation",
        "Built conversational AI agents for streamlined approvals—40% reduction in onboarding time",
        "Established GDPR/CCPA compliance governance frameworks",
        "Developed multi-year architectural roadmap for manufacturing CX"
      ],
      technologies: [
        "Medallion Architecture",
        "MDM",
        "AI/ML",
        "LLMs",
        "AWS",
        "Elasticsearch",
        "API Management"
      ]
    },
    {
      id: 2,
      title: "Enterprise MDM Platform – Charles Schwab",
      role: "Senior Manager – MDM Architect",
      description: "Led a large-scale MDM migration mastering 15 million retail and corporate customer records, replacing legacy APIs with scalable MDM APIs and implementing full CI/CD pipelines.",
      responsibilities: [
        "Onboarded MDM APIs across multiple enterprise applications",
        "Mastered 15 million customer records (Retail & Corporate)",
        "Created multi-year MDM and data quality roadmap",
        "Led CI/CD implementation reducing migration costs significantly",
        "Instituted performance monitoring improving team productivity"
      ],
      technologies: [
        "IBM MDM",
        "Informatica",
        "CI/CD",
        "Oracle",
        "DevOps",
        "REST APIs"
      ]
    },
    {
      id: 3,
      title: "Data Governance Reference Architecture – Dell Technologies",
      role: "Data Governance Architect",
      description: "Established enterprise-wide Data Governance Reference Architecture covering data catalog, lineage, privacy, and quality KPIs. Delivered a Data Dictionary Framework and led vendor selection via RFPs and POCs.",
      responsibilities: [
        "Created Data Governance Reference Architecture",
        "Defined KPIs and metrics for data quality governance",
        "Built data remediation processes to enhance quality",
        "Delivered comprehensive Data Dictionary Framework",
        "Developed RFPs and POCs to guide vendor selection"
      ],
      technologies: [
        "Collibra",
        "Data Catalog",
        "Data Lineage",
        "Data Quality",
        "GDPR",
        "Data Dictionary"
      ]
    },
    {
      id: 4,
      title: "Enterprise Search & Golden Record – AAA Insurance",
      role: "MDM Lead Developer",
      description: "Designed a scalable Elasticsearch-powered enterprise search solution for Policy Center and Agent Center, achieving a unified golden customer record and 99.8% application availability through OpenShift containerization.",
      responsibilities: [
        "Designed enterprise search solution using Elasticsearch",
        "Containerized MDM WAS servers using OpenShift—99.8% availability",
        "Converted ETL jobs from Informatica to Spring Boot",
        "Performed root cause analysis for matching and survivorship",
        "Optimized real-time and batch load performance"
      ],
      technologies: [
        "Elasticsearch",
        "IBM MDM",
        "OpenShift",
        "Spring Boot",
        "Informatica",
        "Oracle"
      ]
    }
  ],

  education: [
    {
      degree: "Bachelor of Engineering – Electronics and Communication Engineering",
      institution: "Prince Shri Venkateshwara Padmavathy Engineering College",
      details: "B.E. (E.C.E.) — Affiliated to Anna University, Chennai.",
      year: ""
    }
  ],

  certifications: [
    {
      title: "Informatica MDM Certified Developer",
      issuer: "Informatica",
      status: "Certified"
    },
    {
      title: "Informatica Certified Developer & Administrator",
      issuer: "Informatica",
      status: "Certified"
    },
    {
      title: "IBM Certified Developer – InfoSphere MDM Server v9.0",
      issuer: "IBM",
      status: "Certified"
    },
    {
      title: "Collibra Certified Expert 1",
      issuer: "Collibra",
      status: "Certified"
    },
    {
      title: "Informatica Cloud Lakehouse Data Management",
      issuer: "Informatica",
      status: "Certified"
    },
    {
      title: "Informatica Cloud Integration for Application Modernization",
      issuer: "Informatica",
      status: "Certified"
    }
  ],

  awards: [
    {
      title: "Top Performer Award",
      organization: "Charles Schwab",
      year: "2022"
    },
    {
      title: "Technical Guru",
      organization: "Cognizant Technology Solutions",
      year: "2011"
    }
  ]
};
